/**
 * The questions people actually ask about Atlas, answered once.
 *
 * Rendered on the landing page and, from the same array, emitted as FAQPage
 * structured data at build time. Keeping one source is not tidiness: search
 * engines require structured answers to match what a visitor can see on the
 * page, and two copies of an answer drift the first time one of them is
 * edited.
 *
 * Answers are written to be quotable on their own, because an answer engine
 * will lift one sentence and attribute it, and a sentence that only makes
 * sense next to the previous one comes out wrong.
 */

export interface FaqEntry {
  question: string;
  /** Plain text. No markup: this is read aloud as often as it is displayed. */
  answer: string;
}

export const FAQ: FaqEntry[] = [
  {
    question: 'What do the heights and colours mean?',
    answer:
      'Height is lines of code on a log scale, so the skyline is the size distribution of the repository. Colour is the top level directory a file belongs to, and each of those becomes a district on its own plate. Footprint deliberately encodes the kind of file rather than its size, because size is already carried by height and spending two channels on one variable wastes one of them.',
  },
  {
    question: 'Does Atlas work on private repositories?',
    answer:
      'No. Atlas reads public repositories only, and it holds no credentials that could reach a private one. Nothing is uploaded and nothing is stored: a map is built on request and rendered in your browser.',
  },
  {
    question: 'How large a repository can it map?',
    answer:
      'A map draws at most 500 files. Larger repositories are not truncated arbitrarily; a prioritisation pass keeps entry points and the highest value source files and samples the rest across directories, so the shape of the project survives even when the file count does not.',
  },
  {
    question: 'Is any of this generated by a language model?',
    answer:
      'No. The pipeline is four deterministic stages: read the repository, classify files and resolve imports, settle a force directed layout, then score centrality and pick landmarks. The same repository always produces the same city, which is what makes two maps worth comparing.',
  },
  {
    question: 'Is this the version that won the hackathon?',
    answer:
      'No. Atlas was built solo in 48 hours for the IBM Bob Hackathon in May 2026 and placed second out of 503 submissions, and that state is permanently tagged v1.0-hackathon in the repository. The judged version drew flat two dimensional maps, had a single theme, and resolved no dependency arcs at all. The isometric renderer, both themes, the file inspector and the credential free ingestion were added after judging closed, on the same four agent pipeline the 48 hours produced.',
  },
  {
    question: 'Can I use a map in an article, a talk or a README?',
    answer:
      'Yes. Atlas is MIT licensed and every map exports to PNG at up to three times scale with a caption band naming the repository. Attribution is appreciated but not required.',
  },
];
